import CAIconComponent from '@ds/components/shared/CAIconComponent'
import { PER_PAGE } from '@vuejs/helpers/_constants'
import { defineComponent } from 'vue'

/**
	 @component CAPagination
	 CAPagination: Numerical Pagination provide only numbers, i.e. itemsPerPage and numberOfRows without actual paginated table data to process pagination

	 * @prop {string} [additionalClasses=''] - Additional classes to apply to the component
	 * @prop {number} [itemsPerPage=20] - The items to display per page
	 * @prop {boolean} [manageUrl=true] - Whether the component should manage URL parameters. Set to false when parent component handles URL state.
	 * @prop {number} [modelValue] - The current page number
	 * @prop {number} numberOfRows - The total number of rows
	 * @prop {string} [overrideScrollTopId=''] - The id to scroll to when the pagination is clicked. Default scrolls to the top of the page. Use `data-scroll-to` and pass in the ID to know where to scroll to.
	 *
	 * @emit {number} update:modelValue - When the current page changes.
	 *
*/

export default defineComponent({
	name: 'CAPagination',

	props: {
		/**
		 * Additional classes to apply to the component.
		 * @type {string}
		 * @default ''
		 */
		additionalClasses: { default: '', type: String },
		/**
		 * The items to display per page.
		 * @type {number}
		 * @default 20
		 */
		itemsPerPage: { default: PER_PAGE.DEFAULT, type: Number },
		/**
		 * Whether the component should manage URL parameters. Set to false when parent component handles URL state.
		 * @type {boolean}
		 * @default true
		 */
		manageUrl: { default: true, type: Boolean },
		/**
		 * The current page number.
		 * @type {number}
		 * @default 1
		 */
		modelValue: { optional: true, type: Number },
		/**
		 * The total number of rows.
		 * @type {number}
		 * @required
		 */
		numberOfRows: { required: true, type: Number },
		/**
		 * The id to scroll to when the pagination is clicked. Default scrolls to the top of the page. Use `data-scroll-to` and pass in the ID to know where to scroll to.
		 * @type {string }
		 * @default ''
		 */
		overrideScrollTopId: { default: '', type: String },
	},

	emits: { 'update:modelValue': (_page: number) => true },

	computed: {
		/**
		 * The current page, synced from modelValue prop (URL is the source of truth)
		 */
		currentPage(): number {
			return this.modelValue || PAGINATION_INDEX
		},

		firstItem(): number {
			return Number(this.offset) + PAGINATION_INDEX
		},

		lastItem(): number {
			return Math.min(this.offset + this.itemsPerPage, this.numberOfRows)
		},

		numberOfPages(): number {
			return Math.ceil(this.numberOfRows / this.itemsPerPage)
		},

		offset(): number {
			const page = Math.min(this.numberOfPages, this.currentPage)

			return (page - PAGINATION_INDEX) * this.itemsPerPage
		},

		pageNumbers(): (number | string)[] {
			const totalPages = this.numberOfPages
			const currentPage = this.currentPage
			const maxPageButtons = 5 // How many pages do we see before ellipsis separates it?

			if (totalPages <= maxPageButtons) {

				return Array.from({ length: totalPages }, (_, i) => i + 1)
			} else {

				const halfMaxButtons = Math.floor(maxPageButtons / 2)
				const startPage = Math.max(1, currentPage - halfMaxButtons)
				const endPage = Math.min(totalPages, currentPage + halfMaxButtons)

				const pages: (number | string)[] = []

				if (startPage > 1) {
					pages.push(1)
					if (startPage > 2) {
						pages.push(ELLIPSIS)
					}
				}

				for (let i = startPage; i <= endPage; i++) {
					pages.push(i)
				}

				if (endPage < totalPages) {
					if (endPage < totalPages - 1) {
						pages.push(ELLIPSIS)
					}
					pages.push(totalPages)
				}

				return pages
			}
		},
	},

	watch: {
		modelValue() {
			this.$nextTick(() => {
				if (this.overrideScrollTopId) {
					const selector = `[data-scroll-top="${this.overrideScrollTopId}"]`
					const container = document.querySelector(selector)

					if (container) {
						container.scrollIntoView({ behavior: 'smooth', block: 'start' })
					}
				} else {
					scrollTo(0, 0)
				}
			})
		},
	},

	mounted() {
		if (this.manageUrl) {
			window.addEventListener('popstate', this.handlePopState)
			this.syncURLToPage()
		}
	},

	beforeUnmount() {
		if (this.manageUrl) {
			window.removeEventListener('popstate', this.handlePopState)
		}
	},

	methods: {
		/**
		 * Navigate to a specific page
		 */
		goToPage(page: number | string) {
			if (page === ELLIPSIS || page === this.currentPage) {
				return
			}

			const pageNumber = Number(page)

			if (this.manageUrl) {
				this.updateURL(pageNumber, false)
			}

			this.$emit('update:modelValue', pageNumber)
		},

		/**
		 * Handle browser back/forward button clicks
		 */
		handlePopState() {
			if (!this.manageUrl) {
				return
			}

			const urlParams = new URLSearchParams(window.location.search)
			const pageFromURL = urlParams.has('page')
				? parseInt(urlParams.get('page')!, 10)
				: PAGINATION_INDEX

			this.$emit('update:modelValue', pageFromURL)
		},

		nextPage(event?: Event) {
			event?.preventDefault()
			if (this.currentPage < this.numberOfPages) {
				this.goToPage(this.currentPage + 1)
			}
		},

		previousPage(event?: Event) {
			event?.preventDefault()
			if (this.currentPage > 1) {
				this.goToPage(this.currentPage - 1)
			}
		},

		/**
		 * Ensure URL reflects current page on initial load
		 */
		syncURLToPage() {
			const urlParams = new URLSearchParams(window.location.search)

			if (!urlParams.has('page')) {
				this.updateURL(PAGINATION_INDEX, true)
			}
		},

		/**
		 * Update the URL with the new page number
		 */
		updateURL(page: number, useReplace: boolean) {
			const urlParams = new URLSearchParams(window.location.search)

			urlParams.set('page', page.toString())

			const newURL = `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`

			if (useReplace) {
				window.history.replaceState({}, '', newURL)
			} else {
				window.history.pushState({}, '', newURL)
			}
		},
	},

	render() {
		return (
			<div class="ca-pagination" data-test="ca-pagination">
				<div class="ca-pagination__mobile">
					<a class="ca-pagination__mobile-link" onClick={this.previousPage}>
						Previous
					</a>

					<span>
						<span class="ca-pagination__page-count">
							<span class="font-medium">{this.firstItem}</span>
							&nbsp;to&nbsp;
							<span class="font-medium">{this.lastItem}</span>
							&nbsp;of&nbsp;
							<span class="font-medium">{this.numberOfRows}</span>
						</span>
					</span>

					<a class="ca-pagination__mobile-link" onClick={this.nextPage}>
						Next
					</a>
				</div>

				<div class="ca-pagination__desktop">
					<div>
						<p class="ca-pagination__page-count">
							Showing&nbsp;
							<span class="font-medium">{this.firstItem}</span>
							&nbsp;to&nbsp;
							<span class="font-medium">{this.lastItem}</span>
							&nbsp;of&nbsp;
							<span class="font-medium">{this.numberOfRows}</span>
						</p>
					</div>

					<div>
						<nav aria-label="Pagination" class="ca-pagination__page-numbers">
							<span
								class="ca-pagination__page-numbers--svg"
								data-test="ca-pagination-previous-page"
								onClick={this.previousPage}
							>
								<span class="sr-only">Previous</span>
								<CAIconComponent icon="SVGChevronLeft" />
							</span>

							{this.pageNumbers.map((pageNumber, index) =>
								<span
									key={pageNumber === '...' ? `'ellipsis'-${index}` : pageNumber}
									aria-current={pageNumber === this.currentPage ? 'page' : undefined}
									aria-label={pageNumber === this.currentPage
										? `Current page, Page ${pageNumber}`
										: `Go to page ${pageNumber}`}
									class={{
										'ca-pagination__page-numbers--number': true,
										'ca-pagination__page-numbers--number-active': pageNumber === this.currentPage,
										'pointer-events-none': pageNumber === '...',
									}}
									data-test={`ca-pagination-page-number-${pageNumber}`}
									tabindex={pageNumber === this.currentPage ? -1 : 0}
									onClick={() => this.goToPage(pageNumber)}
								>
									{pageNumber}
								</span>
							)}

							<span
								class="ca-pagination__page-numbers--svg"
								data-test="ca-pagination-next-page"
								onClick={this.nextPage}
							>
								<span class="sr-only">Next</span>
								<CAIconComponent icon="SVGChevronRight" />
							</span>
						</nav>
					</div>
				</div>
			</div>
		)
	},
})

export const ELLIPSIS = '...'
export const PAGINATION_INDEX = 1
